import { FC, useCallback, useRef, useState } from 'react';
import { Delete as DeleteIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EmailTemplateConfigurationModalProps } from './EmailTemplateConfigurationModal.props';
import { insertTextAtSelection, restoreTextareaSelection } from './insertTextAtSelection';

const EmailTemplateConfigurationModal: FC<EmailTemplateConfigurationModalProps> = ({
  open,
  onClose,
  idPrefix,
  title = 'Configure email template',
  presets,
  emailSubject,
  setEmailSubject,
  introduction,
  setIntroduction,
  introductionPlaceholders = [],
  subjectHelperText,
  introductionHelperText,
  savePresetOpen,
  presetName,
  setPresetName,
  handleOpenSavePreset,
  handleCloseSavePreset,
  handleSavePreset,
  handleApplyPreset,
  handleDeletePreset,
  handleResetToDefault,
}) => {
  const introductionRef = useRef<HTMLTextAreaElement | null>(null);
  const [introductionSelection, setIntroductionSelection] = useState({ start: 0, end: 0 });

  const syncIntroductionSelection = useCallback(() => {
    const element = introductionRef.current;
    if (!element) {
      return;
    }
    setIntroductionSelection({
      start: element.selectionStart ?? introduction.length,
      end: element.selectionEnd ?? introduction.length,
    });
  }, [introduction.length]);

  const handleInsertPlaceholder = useCallback(
    (token: string) => {
      const { nextValue, nextCursor } = insertTextAtSelection(
        introduction,
        token,
        introductionSelection.start,
        introductionSelection.end
      );
      setIntroduction(nextValue);
      setIntroductionSelection({ start: nextCursor, end: nextCursor });
      requestAnimationFrame(() => restoreTextareaSelection(introductionRef.current, nextCursor));
    },
    [introduction, introductionSelection.end, introductionSelection.start, setIntroduction]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        disablePortal
        PaperProps={{
          sx: { maxHeight: '90vh' },
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {presets.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Saved Presets
                </Typography>
                <List
                  dense
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 160,
                    overflow: 'auto',
                  }}
                >
                  {presets.map(preset => (
                    <ListItem
                      key={preset.name}
                      disablePadding
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDeletePreset(preset.name)}
                          aria-label={`Delete preset ${preset.name}`}
                          id={`${idPrefix}-delete-preset-${preset.name}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemButton onClick={() => handleApplyPreset(preset.name)}>
                        <ListItemText primary={preset.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <TextField
              label="Email subject"
              value={emailSubject}
              onChange={event => setEmailSubject(event.target.value)}
              fullWidth
              size="small"
              helperText={subjectHelperText}
              id={`${idPrefix}-subject`}
            />

            <Box>
              {introductionPlaceholders.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                  {introductionPlaceholders.map(placeholder => (
                    <Button
                      key={placeholder.token}
                      size="small"
                      variant="text"
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => handleInsertPlaceholder(placeholder.token)}
                      id={`${idPrefix}-insert-${placeholder.token.replace(/[{}]/g, '')}`}
                      sx={{ minWidth: 0, px: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}
                    >
                      {placeholder.label}
                    </Button>
                  ))}
                </Stack>
              )}
              <TextField
                label="Introduction"
                value={introduction}
                onChange={event => {
                  setIntroduction(event.target.value);
                  syncIntroductionSelection();
                }}
                onClick={syncIntroductionSelection}
                onKeyUp={syncIntroductionSelection}
                onSelect={syncIntroductionSelection}
                inputRef={introductionRef}
                fullWidth
                multiline
                minRows={4}
                helperText={introductionHelperText}
                id={`${idPrefix}-introduction`}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<SaveIcon />}
              onClick={handleOpenSavePreset}
              size="small"
              variant="outlined"
              id={`${idPrefix}-save-preset-button`}
            >
              Save Preset
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleResetToDefault}
              size="small"
              variant="outlined"
              id={`${idPrefix}-reset-template-button`}
            >
              Reset
            </Button>
          </Stack>
          <Button onClick={onClose} variant="contained" id={`${idPrefix}-template-config-done-button`}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={savePresetOpen} onClose={handleCloseSavePreset} disablePortal>
        <DialogTitle>Save email template preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Preset name"
            fullWidth
            value={presetName}
            onChange={event => setPresetName(event.target.value)}
            id={`${idPrefix}-preset-name`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSavePreset}>Cancel</Button>
          <Button
            onClick={handleSavePreset}
            variant="contained"
            disabled={!presetName.trim()}
            id={`${idPrefix}-confirm-save-preset-button`}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailTemplateConfigurationModal;
